import React from 'react';
import {
  WTP_PATTERNS,
  WTP_REASONS,
  WTP_TOPLINE,
  WTP_TOTAL,
  WTP_WINDOW,
} from '../../reports/mockData';
import { PILL_KIND, RPT_TOKENS } from '../../reports/config';
import { Icon } from '../Icon/Icon';
import { ReportPageHeader, SectionHeader } from './ReportShared';
import type { ReportPageProps } from './ReportsPages';

function SentimentBar({ improved, stable, worsened }: { improved: number; stable: number; worsened: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex h-2 overflow-hidden rounded-full border border-[var(--cream-2)]">
        <div style={{ width: `${improved}%`, background: RPT_TOKENS.green }} />
        <div style={{ width: `${stable}%`, background: RPT_TOKENS.ink5 }} />
        <div style={{ width: `${worsened}%`, background: RPT_TOKENS.negative }} />
      </div>
      <div className="flex gap-3 text-[11px] font-medium text-[var(--ink-2)]">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full" style={{ background: RPT_TOKENS.green }} />
          Improved <span className="rpt-mono font-semibold text-[var(--ink-1)]">{improved}%</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-[var(--cream-2)]" />
          Stable <span className="rpt-mono font-semibold text-[var(--ink-1)]">{stable}%</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full" style={{ background: RPT_TOKENS.negative }} />
          Worsened <span className="rpt-mono font-semibold text-[var(--ink-1)]">{worsened}%</span>
        </span>
      </div>
    </div>
  );
}

function PmsMetric({
  label,
  count,
  pct,
  kind = 'neutral',
}: {
  label: string;
  count: number;
  pct: number;
  kind?: 'positive' | 'negative' | 'warning' | 'neutral';
}) {
  const kindColor =
    kind === 'positive'
      ? RPT_TOKENS.greenDeep
      : kind === 'negative'
        ? RPT_TOKENS.negative
        : kind === 'warning'
          ? RPT_TOKENS.warning
          : 'var(--ink-1)';
  return (
    <div className="flex flex-col gap-0.5 rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-2.5">
      <div className="rpt-eyebrow mb-0.5 text-[9.5px]">{label}</div>
      <div className="flex items-baseline gap-1.5">
        <span className="rpt-tab-num text-[20px] font-semibold leading-none tracking-[-0.025em] text-[var(--ink-0)]">
          {count}
        </span>
        <span className="rpt-mono text-[12px] font-semibold" style={{ color: kindColor }}>
          {pct}%
        </span>
      </div>
    </div>
  );
}

function ReasonCard({ r }: { r: (typeof WTP_REASONS)[number] }) {
  const railColor = PILL_KIND[r.railKind].color;
  const resColor =
    r.resolutionRate >= 85 ? RPT_TOKENS.greenDeep : r.resolutionRate >= 70 ? RPT_TOKENS.warning : RPT_TOKENS.negative;
  const toneColor =
    r.tone === 'positive' ? RPT_TOKENS.greenDeep : r.tone === 'warning' ? RPT_TOKENS.warning : 'var(--ink-2)';
  const toneBg =
    r.tone === 'positive'
      ? RPT_TOKENS.greenWash
      : r.tone === 'warning'
        ? 'rgba(181, 128, 61, 0.12)'
        : 'var(--cream-0)';
  const toneBorder =
    r.tone === 'positive'
      ? RPT_TOKENS.greenSoft
      : r.tone === 'warning'
        ? 'rgba(181, 128, 61, 0.20)'
        : 'var(--cream-2)';

  return (
    <div
      className="flex flex-col gap-[18px] rounded-r-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-6 py-[22px] shadow-[var(--sh-1)]"
      style={{ borderLeft: `3px solid ${railColor}` }}
    >
      <div className="flex items-start justify-between gap-3.5">
        <div className="min-w-0">
          <h3 className="mb-1.5 text-[17px] font-semibold leading-[1.25] tracking-[-0.02em] text-[var(--ink-0)]">
            {r.cause}
          </h3>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="rpt-mono text-[12px] font-medium text-[var(--ink-2)] opacity-70">
              {r.pct}% of escalations · avg {r.avgDuration}
            </span>
            {r.hasSettingsLever === 'yes' && (
              <span
                className="rpt-mono inline-flex items-center gap-1 rounded-[5px] px-2 py-px text-[10px] font-bold uppercase tracking-[0.06em]"
                style={{ background: RPT_TOKENS.greenSoft, color: RPT_TOKENS.greenDeep }}
              >
                Settings lever
              </span>
            )}
            {r.hasSettingsLever === 'partial' && (
              <span
                className="rpt-mono inline-flex items-center gap-1 rounded-[5px] px-2 py-px text-[10px] font-bold uppercase tracking-[0.06em]"
                style={{ background: 'rgba(181, 128, 61, 0.12)', color: RPT_TOKENS.warning }}
              >
                Partial lever
              </span>
            )}
          </div>
        </div>
        <div
          className="min-w-[88px] flex-none rounded-[11px] border border-[var(--cream-2)] px-4 py-3 text-center"
          style={{ background: PILL_KIND[r.railKind].bg }}
        >
          <div className="rpt-tab-num text-[26px] font-semibold leading-none tracking-[-0.025em]" style={{ color: railColor }}>
            {r.transferCount}
          </div>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.02em] opacity-85" style={{ color: railColor }}>
            transfers
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3.5">
        <div className="flex flex-col gap-3">
          <div className="rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-2.5">
            <div className="rpt-eyebrow mb-1 text-[9.5px]">Resolution rate</div>
            <div className="rpt-tab-num text-[22px] font-semibold leading-none tracking-[-0.025em]" style={{ color: resColor }}>
              {r.resolutionRate}%
            </div>
            <div className="mt-1 text-[11px] text-[var(--ink-2)] opacity-70">transcript-judged complete</div>
          </div>
          <div className="rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-2.5">
            <div className="rpt-eyebrow mb-2 text-[9.5px]">Sentiment outcome</div>
            <SentimentBar {...r.sentiment} />
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <PmsMetric
            label="PMS bookings · 24h"
            count={r.pmsBookings}
            pct={r.pmsBookingsPct}
            kind={r.pmsBookingsPct >= 60 ? 'positive' : r.pmsBookingsPct >= 30 ? 'warning' : 'negative'}
          />
          <PmsMetric
            label="Callback rate · 7d"
            count={r.pmsCallbacks}
            pct={r.pmsCallbacksPct}
            kind={r.pmsCallbacksPct <= 10 ? 'positive' : r.pmsCallbacksPct <= 18 ? 'warning' : 'negative'}
          />
        </div>
      </div>

      <div
        className="flex items-start gap-2.5 rounded-[10px] px-3.5 py-3"
        style={{ background: toneBg, border: `1px solid ${toneBorder}` }}
      >
        <Icon
          kind={r.tone === 'positive' ? 'check-circ' : 'alert'}
          size={13}
          color={toneColor}
          stroke={2}
          className="mt-0.5 shrink-0"
        />
        <p className="m-0 text-[13px] font-medium leading-[1.55] tracking-[-0.005em] text-[var(--ink-1)]">
          {r.insight}
        </p>
      </div>
    </div>
  );
}

function PatternInsightCard({ p }: { p: (typeof WTP_PATTERNS)[number] }) {
  const railColor = PILL_KIND[p.railKind].color;
  return (
    <div
      className="rounded-r-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-[22px] py-5 shadow-[var(--sh-1)]"
      style={{ borderLeft: `3px solid ${railColor}` }}
    >
      <div className="rpt-eyebrow mb-1.5 text-[10px]" style={{ color: railColor }}>
        {p.eyebrow}
      </div>
      <h4 className="mb-2.5 text-base font-semibold leading-[1.3] tracking-[-0.02em] text-[var(--ink-0)]">
        {p.title}
      </h4>
      <p className="m-0 text-[13px] leading-[1.65] tracking-[-0.005em] text-[var(--ink-1)]">{p.body}</p>
    </div>
  );
}

function WTPToplineTile({
  data,
}: {
  data: (typeof WTP_TOPLINE)[number];
}) {
  const kindColor =
    data.trendKind === 'positive'
      ? RPT_TOKENS.greenDeep
      : data.trendKind === 'negative'
        ? RPT_TOKENS.negative
        : RPT_TOKENS.ink3;
  return (
    <div className="rpt-kpi-card rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-[18px] py-4 pb-[18px] shadow-[var(--sh-1)]">
      <div className="mb-2.5 flex items-center justify-between">
        <div className="rpt-eyebrow text-[10px]">{data.label}</div>
        <span
          className="rpt-mono rounded px-[7px] py-px text-[10px] font-semibold uppercase tracking-[0.04em]"
          style={{
            color: data.source === 'pms' ? RPT_TOKENS.brassDeep : RPT_TOKENS.greenDeep,
            background: data.source === 'pms' ? RPT_TOKENS.brassTint : RPT_TOKENS.greenSoft,
          }}
        >
          {data.source}
        </span>
      </div>
      <div className="rpt-tab-num mb-1.5 text-[38px] font-semibold leading-none tracking-[-0.03em] text-[var(--ink-0)]">
        {data.value}
      </div>
      <div className="mb-1 flex items-center gap-1.5">
        <span className="rpt-mono text-[12px] font-semibold" style={{ color: kindColor }}>
          {data.trend}
        </span>
        <span className="text-[11.5px] text-[var(--ink-2)] opacity-70">vs last week</span>
      </div>
      <div className="text-[11.5px] leading-normal tracking-[-0.005em] text-[var(--ink-2)]">{data.sub}</div>
    </div>
  );
}

export function WarmTransferPerformanceReportPage({ timeRange, onTimeRangeChange }: ReportPageProps) {
  return (
    <div className="rpt-fade-up rpt-d-0">
      <ReportPageHeader
        eyebrow="Reports · handoffs"
        title="Warm transfer"
        italic="performance"
        description="How calls perform after Elva transfers them to your team. Elva continues to record and analyze each call after handoff — this is the aggregate picture, no individual breakdown."
        metric={WTP_TOTAL}
        metricLabel={`Transfers handled · ${WTP_WINDOW}`}
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
      />

      <div className="mb-7 grid grid-cols-4 gap-3">
        {WTP_TOPLINE.map((tile) => (
          <WTPToplineTile key={tile.id} data={tile} />
        ))}
      </div>

      <section className="rpt-fade-up rpt-d-1 mb-8">
        <SectionHeader
          num="01"
          title="By reason"
          subtitle="Seven escalation categories — sentiment outcome, PMS-derived booking, callback rate, and Elva's read on each."
        />
        <div
          className="grid gap-3.5"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))' }}
        >
          {WTP_REASONS.map((r) => (
            <ReasonCard key={r.id} r={r} />
          ))}
        </div>
      </section>

      <section className="rpt-fade-up rpt-d-2 mb-8">
        <SectionHeader
          num="02"
          accent="brass"
          title={
            <>
              What this <span className="rpt-serif text-[22px] font-normal">means</span>
            </>
          }
          subtitle="Three patterns Elva noticed in this week's transfers. The narrative read of the numbers above."
        />
        <div className="flex flex-col gap-3">
          {WTP_PATTERNS.map((p) => (
            <PatternInsightCard key={p.eyebrow} p={p} />
          ))}
        </div>
      </section>
    </div>
  );
}
